import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { deleteItems, updateStateData } from "../../../Store/actions";
import { Box } from "@mui/material";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { fetchClipNames, loadClips } from "../../../api_calls/clipRequests";
import { checkUserDetailsFromCookie, checkUserRole } from "../../../api_calls/userManagement";
import { useNavigate } from "react-router-dom";
import { createCookie, deleteCookie } from "../../../api_calls/requestConfig";
import { LISTING_RELOAD } from "../../../api_calls/listingReload";
import useListingOptionsVersion from "../../../hooks/useListingOptionsVersion";
import FilterPanel from "../../../components/commonComponents/Filters/FilterPanel";
import { fetchCampaignNames, fetchOwnerNames, fetchRetailerRoots } from "../../../api_calls/campaignRequests";
import { fetchStoreNames } from "../../../api_calls/infrastructure";
import { collectIdsBySharedName } from "../../../api_calls/utils";

const ClipListFilters = (props) => {
  const Clips = useSelector((state) => state.userSelections);
  const verifyUserRoleName = checkUserRole();
  const navigate = useNavigate();
  const dispatchAction = useDispatch();

  const optionsVersion = useListingOptionsVersion(Clips, LISTING_RELOAD.clips, Clips.clips_list);

  // Same user types that see the Retailer listing column
  const checkUserDetails = checkUserDetailsFromCookie();
  const userType =
    checkUserDetails.success === true ? checkUserDetails.user_detail.user_type : "";
  const canUseRetailerFilter = ["cyreen", "tenant"].includes(userType);

  const saveClipFilters = (filters) => {
    const selectedClipNameIds = filters.clip_name || [];

    const filterValues = {
      campaign_name: filters.campaign_name || [],
      // keep the deduplicated selection for the drawer/summary display
      clip_name: selectedClipNameIds,
      // and the full set of ids sharing each chosen name for the backend query,
      // so duplicate visual names return all matching records (see loadClips)
      clip_name_ids: collectIdsBySharedName(selectedClipNameIds, Clips?.clip_names_info?.clip_names_list),
      clip_company_name: filters.owner || [],
      // Drawer key "retailer" → Redux root_retailer_filter
      root_retailer_filter: filters.retailer || [],
      status_filter: filters.status || [],
      clip_store_filter: filters.store_name || [],
      clip_visual_type: filters.clip_visual_type || null,
    };
    const noFiltersSelected =
      (!filterValues.campaign_name || filterValues.campaign_name.length === 0) &&
      (!filterValues.clip_name || filterValues.clip_name.length === 0) &&
      (!filterValues.clip_company_name || filterValues.clip_company_name.length === 0) &&
      (!filterValues.root_retailer_filter || filterValues.root_retailer_filter.length === 0) &&
      (!filterValues.status_filter || filterValues.status_filter.length === 0) &&
      (!filterValues.clip_store_filter || filterValues.clip_store_filter.length === 0) &&
      (!filterValues.clip_visual_type)
    if (noFiltersSelected) {
      // If nothing is selected, clear existing filters and return
      clearfilters();
      return;
    }
    // Persist to Redux for future retrieval (e.g. report generation)
    dispatchAction(
      updateStateData({
        clip_list_filters: filterValues,
        clips_list_filter_applied: true,
      })
    );
    // Persist to cookies for session persistence
    createCookie("clip_list_filters", JSON.stringify(filterValues));
    // Reload clips using the current page size (reset to page 1)
    loadClips(0, Clips.clips_pagination.pageSize);
  };

  const clearfilters = () => {
    // Remove filter cookie
    deleteCookie("clip_list_filters", true);
    // Clear state in Redux (removes filters and resets flagged state)
    dispatchAction(deleteItems(["clip_list_filters", "filtered_clips_list", "clips_list_filter_applied"]));
    // Reload unfiltered list
    loadClips(0, Clips.clips_pagination.pageSize);
  };

  // silent skips the page backdrop. The open list shows its own spinner.
  const clipFiltersConfig = React.useMemo(
    () => [
      {
        key: "clip_name",
        label: "Visual Name",
        type: "multi",
        loadOptions: () => fetchClipNames({ silent: true }),
        options: () => Clips?.clip_names_info?.clip_names_list,
      },
      {
        key: "campaign_name",
        label: "Campaign Name",
        type: "multi",
        loadOptions: () => fetchCampaignNames({ silent: true }),
        options: () =>
          typeof Clips?.campaign_names_info !== "undefined"
            ? Clips?.campaign_names_info?.campaign_name_obj
            : [],
      },
      {
        key: "status",
        label: "Progress",
        disableSearch: true,
        type: "multi",
        selectionParam: "label",
        disableSearch: true,
        options: () => [
          { value: 1, label: "Active" },
          { value: 2, label: "Scheduled" },
          { value: 3, label: "Completed" },
          { value: 4, label: "Stopped" },
        ],
      },
      {
        key: "owner",
        label: "Owner",
        type: "multi",
        loadOptions: () => fetchOwnerNames("list-clips", { silent: true }),
        options: () => Clips?.companies_info,
      },
      {
        // Drawer key "retailer"; Redux key root_retailer_filter
        key: "retailer",
        label: "Retailer",
        type: "multi",
        loadOptions: () => fetchRetailerRoots({ silent: true }),
        options: () => Clips?.retailer_roots_info,
        enabled: canUseRetailerFilter,
      },
      {
        key: "store_name",
        label: "Stores",
        type: "multi",
        showDetailedStoreSelection: true,
        // Group by retailer when the shared hierarchy rule matches. Saved values stay store ids.
        groupStoresByRetailer: true,
        loadOptions: () => fetchStoreNames({ silent: true }),
        options: () => Clips?.store_names_info?.store_names_list,
      },
      {
        key: "clip_visual_type",
        label: "Type",
        disableSearch: true,
        type: "single",
        selectionParam: "value",
        options: () => [
          { value: 'commercial', label: 'Commercial' },
          { value: 'unlimited', label: 'Unlimited' },
        ],
      },
      // {
      //   key: "clip_visual_type",
      //   label: "Type",
      //   type: "single",
      //   selectionParam: "value",
      //   options: () => [
      //     { value: 'commercial', label: 'Commercial' },
      //     { value: 'unlimited', label: 'Unlimited' },
      //   ],
      // },
    ],
    [Clips, canUseRetailerFilter]
  );

  // Map Redux state filters to FilterPanel expected keys
  const getMappedInitialFilters = () => {
    if (!Clips.clip_list_filters) {
      return {};
    }

    return {
      clip_name: Clips.clip_list_filters.clip_name || [],
      campaign_name: Clips.clip_list_filters.campaign_name || [],
      status: Clips.clip_list_filters.status_filter || [],
      owner: Clips.clip_list_filters.clip_company_name || [],
      retailer: Clips.clip_list_filters.root_retailer_filter || [],
      store_name: Clips.clip_list_filters.clip_store_filter || [],
      clip_visual_type: Clips.clip_list_filters.clip_visual_type || null,
    };
  };

  const overviewButton = verifyUserRoleName.user_role !== "Brand" ? (
    <ActionButton
      onClick={() => {
        navigate("/check-campaigns/visuals-overview");
      }}
      label={"Overview"}
    />
  ) : null;

  return (
    <Box>
      <FilterPanel
        tableId="clip_list"
        optionsVersion={optionsVersion}
        filtersConfig={clipFiltersConfig}
        applyFilterFunc={saveClipFilters}
        clearFilterFunc={clearfilters}
        initiallyAppliedVals={getMappedInitialFilters()}
        draftKeyForStore="store_name"
        rightAction={overviewButton}
      />
    </Box>
  );
};

export default ClipListFilters;
